

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      // A request still in flight is for the previous selection - drop it so its
      // response cannot overwrite what the customer is choosing right now.
      FacetFiltersForm.abortPendingRequest();
      this.debouncedOnSubmit(event);
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    // facetForm.querySelector('.matat-filters__close').addEventListener('click', ()=> {
    //   facetForm.querySelector('.filter-opener').dispatchEvent(new Event('click'));
    // });
    this.shortbyjs();
    
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static abortPendingRequest() {
    if (FacetFiltersForm.abortController) FacetFiltersForm.abortController.abort();
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.abortPendingRequest();
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer){
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    const abortController = new AbortController();
    FacetFiltersForm.abortController = abortController;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Filter request failed with status ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (abortController.signal.aborted) return;
        FacetFiltersForm.renderSection(html);
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        // A failed request or render used to leave the grey overlay up with every
        // filter disabled. The URL already holds the selection, so load it in full.
        window.location.reload();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    FacetFiltersForm.renderSection(FacetFiltersForm.filterData.find(filterDataUrl).html);
  }

  // The filter column, mobile drawer, pills and count all live inside
  // #ProductGridContainer, so replacing it updates everything in one go.
  static renderSection(html) {
    const source = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    if (!source) throw new Error('Filter response has no #ProductGridContainer');

    const container = document.getElementById('ProductGridContainer');
    const uiState = FacetFiltersForm.getUiState(container);
    container.innerHTML = source.innerHTML;
    FacetFiltersForm.restoreUiState(container, uiState);
  }

  // Key each <details> by its form and filter name rather than its id: ids are
  // numbered by position, and Shopify leaves out groups that have no values for the
  // current results, which shifts the numbers (the price groups also share one id).
  static getFilterDetails(container) {
    return Array.from(container.querySelectorAll('#main-collection-filters details')).map((details) => {
      const form = details.closest('form');
      const input = details.querySelector('input[name]');
      return [`${form ? form.id : details.classList[0]}|${input ? input.name : details.id}`, details];
    });
  }

  // Re-rendering closed whatever the customer had open - the filter group they were
  // using and, on mobile, the whole filter drawer - so remember it to put it back.
  static getUiState(container) {
    const active = document.activeElement;
    let focusSelector = '';
    if (active && container.contains(active)) {
      if (active.id) {
        focusSelector = `#${CSS.escape(active.id)}`;
      } else if (active.name && active.form && active.form.id) {
        focusSelector = `#${CSS.escape(active.form.id)} [name="${CSS.escape(active.name)}"]`;
      }
    }

    return {
      details: FacetFiltersForm.getFilterDetails(container).map(([key, details]) => [key, {
        open: details.open,
        opening: details.classList.contains('menu-opening'),
        expanded: details.querySelector('summary')?.getAttribute('aria-expanded'),
      }]),
      showMoreIds: Array.from(container.querySelectorAll('show-more-button'))
        .filter((showMore) => showMore.querySelector('.label-show-less:not(.hidden)'))
        .map((showMore) => showMore.querySelector('button').id),
      focusSelector,
    };
  }

  static restoreUiState(container, uiState) {
    const savedDetails = new Map(uiState.details);
    FacetFiltersForm.getFilterDetails(container).forEach(([key, details]) => {
      const saved = savedDetails.get(key);
      if (!saved) return;
      details.open = saved.open;
      details.classList.toggle('menu-opening', saved.opening);
      const summary = details.querySelector('summary');
      if (summary && saved.expanded !== null && saved.expanded !== undefined) summary.setAttribute('aria-expanded', saved.expanded);
    });

    uiState.showMoreIds.forEach((id) => {
      const button = id && document.getElementById(id);
      const showMore = button && button.closest('show-more-button');
      if (showMore && typeof showMore.expandShowMore === 'function') showMore.expandShowMore({ target: button });
    });

    const focusTarget = uiState.focusSelector && container.querySelector(uiState.focusSelector);
    if (focusTarget) focusTarget.focus({ preventScroll: true });
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    // The price slider always posts both ends. Leave out an end still at its limit,
    // otherwise any filter click also applied (and showed) a full-range price filter.
    form.querySelectorAll('collection-filter-price input[type="range"]').forEach((input) => {
      const atLimit = input.dataset.element === 'input-min'
        ? input.valueAsNumber <= Number(input.min)
        : input.valueAsNumber >= Number(input.max);
      if (atLimit) formData.delete(input.name);
    });
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  shortbyjs(){
    const element = document.getElementById('SortBy');
    const choices = new Choices(element, {
      searchEnabled: false,
      itemSelectText: '',
      allowHTML: true
    });
    document.querySelector('#SortBy').addEventListener('change', function(e){
      const currentURL = window.location.href;
      const url = new URL(currentURL);
      const searchParams = url.searchParams;
      let new_search = '';
      searchParams.forEach((value, key) => {
          if (key === 'sort_by') {
              new_search += `&${key}=${document.querySelector('#SortBy').value}`;
          } else {
              new_search += `&${key}=${value}`;
          }
      });
      new_search = new_search.startsWith('&') ? new_search.substring(1) : new_search;
      if(new_search == ""){
        new_search=`&sort_by=${document.querySelector('#SortBy').value}`;
      }
      console.log(new_search)
      FacetFiltersForm.renderPage(new_search);
    });
    document.querySelector('#FacetFiltersForm').classList.add('loaded');
    document.querySelector('#ProductGridContainer').classList.add('loaded');
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event)
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class CollectionFilterPrice extends HTMLElement {
    constructor() {
        super();
        this.elements = {
            inputMin: '[data-element="input-min"]',
            inputMax: '[data-element="input-max"]'
        };
    }

    connectedCallback() {
        this.$inputMin = this.querySelector(this.elements.inputMin);
        this.$inputMax = this.querySelector(this.elements.inputMax);
        this.$valueMin = this.querySelector('.collection-filter-price-values-min');
        this.$valueMax = this.querySelector('.collection-filter-price-values-max');

        if (this.$inputMin && this.$inputMax) {
            this._initSlider();
            this._setRangeStyles();
        }
    }

    _initSlider() {
        this.$inputMin.addEventListener('change', this._sliderChangeHandler.bind(this));
        this.$inputMin.addEventListener('input', this._handleInput.bind(this));
        this.$inputMax.addEventListener('change', this._sliderChangeHandler.bind(this));
        this.$inputMax.addEventListener('input', this._handleInput.bind(this));
    }

    _sliderChangeHandler() {
        this.dispatchEvent(new CustomEvent('filter:change', { bubbles: true }));
    }

    _handleInput({ target }) {
        this._checkInputsIntersection(target);
        this._setRangeStyles();
        this._setValueLabels();
    }

    // The labels come from the server, so while dragging they kept showing the old
    // range and did not match the thumbs.
    _setValueLabels() {
        if (this.$valueMin) this.$valueMin.textContent = this.$valueMin.textContent.replace(/[\d.,]+/, this.minValue.toFixed(2));
        if (this.$valueMax) this.$valueMax.textContent = this.$valueMax.textContent.replace(/[\d.,]+/, this.maxValue.toFixed(2));
    }

    _checkInputsIntersection(target) {
        if (target === this.$inputMin && this.minValue > this.maxValue) {
            this.$inputMin.value = this.maxValue;
        }
        if (target === this.$inputMax && this.maxValue < this.minValue) {
            this.$inputMax.value = this.minValue;
        }
    }

    _setRangeStyles() {
        const maxRange = this.$inputMax.max ? parseFloat(this.$inputMax.max) : 100;
        const rangeMin = (this.minValue / maxRange) * 100;
        const rangeMax = (this.maxValue / maxRange) * 100;

        // Clamp values to 0–100
        const safeMin = Math.min(Math.max(rangeMin, 0), 100);
        const safeMax = Math.min(Math.max(rangeMax, 0), 100);

        this.style.setProperty('--range-min', `${safeMin}%`);
        this.style.setProperty('--range-max', `${safeMax}%`);
    }

    get minValue() {
        return this.$inputMin?.valueAsNumber || 0;
    }

    get maxValue() {
        return this.$inputMax?.valueAsNumber || 0;
    }
}

customElements.define('collection-filter-price', CollectionFilterPrice);



class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
